import { siteConfig } from '@/constants/site'

export type HelpFaq = {
    question: string
    answer: string
}

/* ------------------------------ Support form ----------------------------- */

export type SupportTopic = 'soporte' | 'ventas' | 'facturacion'

export type SupportPlan = 'gratis' | 'pro' | 'empresarial'

export const SUPPORT_TOPICS: Array<{ value: SupportTopic, label: string, hint: string, messageLabel: string }> = [
    { value: 'soporte', label: 'Soporte técnico', hint: 'Dudas de uso, errores e impresoras', messageLabel: '¿Qué pasa y en qué pantalla lo ves?' },
    { value: 'ventas', label: 'Ventas y planes', hint: 'Planes, precios y prueba gratis', messageLabel: '¿Qué necesita tu negocio?' },
    { value: 'facturacion', label: 'Facturación y cuenta', hint: 'Pagos, suscripción y acceso', messageLabel: 'Cuéntanos sobre tu pago o tu cuenta' }
]

export const SUPPORT_PLANS: Array<{ value: SupportPlan, label: string }> = [
    { value: 'gratis', label: 'Gratis' },
    { value: 'pro', label: 'Pro' },
    { value: 'empresarial', label: 'Empresarial' }
]

export const topicLabel = (value: string): string => SUPPORT_TOPICS.find(topic => topic.value === value)?.label ?? value

export const planLabel = (value: string): string => SUPPORT_PLANS.find(plan => plan.value === value)?.label ?? value

/* ------------------------------ Contact page ----------------------------- */

export const supportSteps = [
    { stamp: 'Desde esta página', title: 'Envías tu solicitud', body: 'Completa el formulario o escríbenos por correo. Tu mensaje queda registrado como un ticket.' },
    { stamp: 'Equipo Himelio', title: 'La revisamos', body: 'El equipo de Himelio la atiende según el tema: soporte técnico, ventas y planes, o facturación y cuenta.' },
    { stamp: 'En tu bandeja', title: 'Te respondemos por correo', body: 'La respuesta llega a la dirección de correo que dejaste en tu solicitud.' }
]

export const contactFaqs: Array<HelpFaq> = [
    {
        question: '¿Cómo contacto a soporte?',
        answer: `Escríbenos a ${siteConfig.email} o abre un ticket desde esta página. Cada solicitud queda registrada y te respondemos por correo.`
    },
    {
        question: '¿Tienen prueba gratis?',
        answer: 'Sí. Puedes usar el Plan Pro durante 14 días, sin tarjeta de crédito.'
    },
    {
        question: '¿Himelio se conecta con la DGII?',
        answer: 'Con el Asistente Mensual DGII preparas cada mes el formato 607. Himelio no está certificado por la DGII ni envía e-CF por webservice: tú presentas los archivos en la Oficina Virtual (OFV).'
    }
]

/* ------------------------------- Help center ------------------------------ */

export type HelpGuideIcon = 'start' | 'fiscal' | 'pos' | 'printer' | 'install' | 'plans'

export const helpGuides: Array<{ href: string, title: string, body: string, icon: HelpGuideIcon }> = [
    { href: '/docs/getting-started', title: 'Guía de inicio rápido', body: 'Crea tu cuenta, configura tu negocio y emite tu primera factura.', icon: 'start' },
    { href: '/docs/fiscal-607', title: 'Asistente DGII 607', body: 'Genera cada mes el TXT oficial del 607 y el resumen de consumo.', icon: 'fiscal' },
    { href: '/docs/pos', title: 'Caja y punto de venta', body: 'Vende desde el mostrador, cobra y lleva el control de tu caja.', icon: 'pos' },
    { href: '/docs/devices', title: 'Impresoras térmicas ESC/POS', body: 'Conecta impresoras de 58 u 80 mm por USB, red o WiFi.', icon: 'printer' },
    { href: '/docs/install-devices', title: 'Instalar en tus dispositivos', body: 'Instala Himelio desde el navegador en tu computadora, teléfono o tablet.', icon: 'install' },
    { href: '/docs/subscription-plans', title: 'Planes y prueba gratis', body: 'Compara los planes, prueba el Plan Pro 14 días y cambia cuando quieras.', icon: 'plans' }
]

export const helpFaqs: Array<HelpFaq> = [
    {
        question: '¿Qué puedo gestionar con Himelio?',
        answer: 'Ventas, facturas comerciales, la descarga mensual del formato DGII 607, inventario con Kardex, clientes, cotizaciones y reparaciones. Todo desde una sola cuenta.'
    },
    {
        question: '¿Himelio se conecta con la DGII?',
        answer: 'Sí, con el Asistente Mensual DGII, que genera el archivo TXT oficial del 607 y un resumen de consumo. Himelio no está certificado por la DGII ni envía e-CF por webservice: tú presentas los archivos en la Oficina Virtual (OFV).'
    },
    {
        question: '¿Qué tipos de comprobantes puedo emitir?',
        answer: 'Facturas comerciales y comprobantes tradicionales como B01, B02, B14 y B15. Cada mes descargas tus facturas y el TXT del 607 para subirlo a la OFV.'
    },
    {
        question: '¿Tienen prueba gratis?',
        answer: 'Sí. Tienes 14 días del Plan Pro para probarlo todo, sin tarjeta de crédito.'
    },
    {
        question: '¿Qué impresoras térmicas de tickets son compatibles?',
        answer: 'Cualquier impresora térmica ESC/POS de 58 u 80 mm, conectada por USB, red o WiFi.'
    },
    {
        question: '¿Puedo cambiar de plan en cualquier momento?',
        answer: 'Sí. Puedes subir de plan o pausar tu suscripción desde la configuración de tu cuenta.'
    },
    {
        question: '¿Puedo usar Himelio en mi teléfono?',
        answer: 'Sí. Himelio se instala desde el navegador en Windows, Mac, Linux, Chromebook, Android, iPhone y iPad, sin costo adicional.'
    },
    {
        question: '¿Cómo contacto a soporte?',
        answer: `Escríbenos a ${siteConfig.email} o abre un ticket desde la página de contacto. Te respondemos por correo.`
    }
]
